import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { FreeAssetRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { sumWidth, TipText } from '@hq/ui-kit';
import { hq, useAuthStore } from '../session';

const PAGE_SIZE = 20;

const ASSET_TYPE_META: Record<string, string> = {
  PRINTER: '腕带打印机',
  CARBON_RIBBON: '碳带',
  WRISTBAND: '腕带',
  CARD_CLIP: '卡扣',
  OTHER: '其他配件'
};

const STATUS_META: Record<string, { text: string; color: string }> = {
  ISSUED: { text: '已发放', color: 'green' },
  RETURNED: { text: '已退回', color: 'orange' }
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.text }));

interface FreeAssetFormValues {
  assetType: string;
  assetName: string;
  spec?: string;
  qty: number;
  unit?: string;
  providerName?: string;
  issueDate?: Dayjs;
  receiverOrgId?: string;
  receiverDeptId?: string;
  voucherUrl?: string;
  remark?: string;
}

function StatusTag({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { text: status || '-', color: 'default' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
}

/**
 * 赠品 / 免费资产发放台账（`wl_free_asset`）：记录中标方免费提供的腕带打印机、
 * 碳带、卡扣、维修配件等资产的发放与退回，支撑"提供核验 + 后期增设"。
 */
export default function FreeAssetOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage =
    profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'wl:purchase:manage') : false);

  const [keyword, setKeyword] = useState('');
  const [assetType, setAssetType] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [receiverOrgId, setReceiverOrgId] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FreeAssetRow | null>(null);
  const [form] = Form.useForm<FreeAssetFormValues>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'free-asset', keyword, assetType, status, receiverOrgId],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.freeAsset.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        assetType,
        status,
        receiverOrgId
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const orgs = useQuery({
    queryKey: ['ops', 'org', 'options'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.org.options({})
  });

  const formReceiverOrgId = Form.useWatch('receiverOrgId', form);
  const depts = useQuery({
    queryKey: ['ops', 'dept', 'options', formReceiverOrgId],
    enabled: open && Boolean(formReceiverOrgId),
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.orgStructure.dept.list({ orgId: formReceiverOrgId })
  });

  const save = useMutation({
    mutationFn: (payload: { id?: string; values: FreeAssetFormValues }) => {
      const values = payload.values;
      return hq.freeAsset.save({
        id: payload.id,
        assetType: values.assetType,
        assetName: values.assetName.trim(),
        qty: values.qty,
        spec: values.spec?.trim() || null,
        unit: values.unit?.trim() || null,
        providerName: values.providerName?.trim() || null,
        issueDate: values.issueDate ? values.issueDate.second(0).millisecond(0).toISOString() : null,
        receiverOrgId: values.receiverOrgId,
        receiverDeptId: values.receiverDeptId,
        voucherUrl: values.voucherUrl?.trim() || null,
        remark: values.remark?.trim() || null
      });
    },
    onSuccess: async (result) => {
      message.success(result.created ? '台账已登记' : '台账已更新');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'free-asset'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const returnAsset = useMutation({
    mutationFn: (id: string) => hq.freeAsset.returnAsset(id, {}),
    onSuccess: async () => {
      message.success('已登记退回');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'free-asset'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '退回登记失败');
    }
  });

  const orgNameMap = new Map((orgs.data ?? []).map((row) => [row.id, row.orgName]));

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ assetType: 'PRINTER', qty: 1, unit: '台', issueDate: dayjs() });
    setOpen(true);
  };

  const openEdit = (row: FreeAssetRow) => {
    setEditing(row);
    form.setFieldsValue({
      assetType: row.assetType,
      assetName: row.assetName,
      spec: row.spec ?? undefined,
      qty: row.qty,
      unit: row.unit,
      providerName: row.providerName ?? undefined,
      issueDate: dayjs(row.issueDate),
      receiverOrgId: String(row.receiverOrgId) === '0' ? undefined : row.receiverOrgId,
      receiverDeptId: String(row.receiverDeptId) === '0' ? undefined : row.receiverDeptId,
      voucherUrl: row.voucherUrl ?? undefined,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<FreeAssetRow> = [
    {
      title: '台账编号',
      dataIndex: 'bizNo',
      width: 190,
      render: (value: string) => <Typography.Text code>{value}</Typography.Text>
    },
    {
      title: '资产名称',
      key: 'asset',
      width: 220,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <TipText text={row.assetName} />
          <Typography.Text type="secondary">{row.spec || '未填规格'}</Typography.Text>
        </Space>
      )
    },
    {
      title: '类型',
      dataIndex: 'assetType',
      width: 120,
      render: (value: string) => <Tag>{ASSET_TYPE_META[value] ?? value}</Tag>
    },
    {
      title: '数量',
      key: 'qty',
      width: 110,
      align: 'right',
      render: (_value: unknown, row) => `${row.qty} ${row.unit}`
    },
    {
      title: '受赠机构 / 科室',
      key: 'receiver',
      width: 200,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>
            {orgNameMap.get(row.receiverOrgId) ||
              (String(row.receiverOrgId) === '0' ? '未指定' : `机构 #${row.receiverOrgId}`)}
          </Typography.Text>
          <Typography.Text type="secondary">
            {String(row.receiverDeptId) === '0' ? '机构层面' : `科室 #${row.receiverDeptId}`}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: '提供方',
      key: 'provider',
      width: 170,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.providerName || '中标方'}</Typography.Text>
          <Typography.Text type="secondary">
            {row.providerFree ? '免费提供' : '非免费'}
            {row.settleZeroFee ? ' · 结算 0 元' : ''}
          </Typography.Text>
        </Space>
      )
    },
    { title: '发放日期', dataIndex: 'issueDate', width: 120, render: (value: string) => dayjs(value).format('YYYY-MM-DD') },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => <StatusTag status={value} />
    },
    {
      title: '凭证',
      dataIndex: 'voucherUrl',
      width: 120,
      render: (value: string | null) =>
        value ? (
          <Typography.Link href={value} target="_blank" rel="noreferrer">
            查看凭证
          </Typography.Link>
        ) : (
          <Typography.Text type="secondary">未上传</Typography.Text>
        )
    },
    { title: '备注', dataIndex: 'remark', width: 160, ellipsis: { showTitle: false }, render: (value: string | null) => <TipText text={value || '-'} /> },
    { title: '更新时间', dataIndex: 'updateAt', width: 170, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_value: unknown, row) => {
        if (!canManage) return <Typography.Text type="secondary">只读</Typography.Text>;
        if (row.status === 'RETURNED') return <Typography.Text type="secondary">已退回</Typography.Text>;
        return (
          <Space size={4}>
            <Button type="link" size="small" onClick={() => openEdit(row)}>
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              danger
              loading={returnAsset.isPending}
              onClick={() => returnAsset.mutate(row.id)}
            >
              退回
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <Card className="page-card" title="赠品 / 免费资产台账">
      <Typography.Paragraph type="secondary">
        记录中标方免费提供的腕带打印机、碳带、卡扣、维修配件等资产（`wl_free_asset`）：登记发放对象与数量，
        退回后置为「已退回」。台账用于核验"免费提供"承诺与后期增设，结算侧按 0 元出账。
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="按编号/名称/规格/提供方搜索"
          style={{ width: 260 }}
          onSearch={(value) => setKeyword(value.trim())}
          enterButton
        />
        <Select
          allowClear
          placeholder="按类型筛选"
          style={{ width: 160 }}
          value={assetType}
          onChange={(next) => setAssetType(next)}
          options={Object.entries(ASSET_TYPE_META).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="按状态筛选"
          style={{ width: 130 }}
          value={status}
          onChange={(next) => setStatus(next)}
          options={STATUS_OPTIONS}
        />
        <Select
          allowClear
          placeholder="按受赠机构筛选"
          style={{ width: 200 }}
          value={receiverOrgId}
          onChange={(next) => setReceiverOrgId(next)}
          options={(orgs.data ?? []).map((row) => ({ value: row.id, label: row.orgName }))}
        />
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
        {canManage ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            登记发放
          </Button>
        ) : null}
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="台账加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<FreeAssetRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无发放记录' }}
        onRow={(row) => ({
          onDoubleClick: canManage && row.status === 'ISSUED' ? () => openEdit(row) : undefined
        })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? `维护台账：${editing.bizNo}` : '登记免费资产发放'}
        open={open}
        width={760}
        destroyOnHidden
        confirmLoading={save.isPending}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={async () => {
          const values = await form.validateFields();
          save.mutate({ id: editing?.id, values });
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assetType" label="资产类型" rules={[{ required: true, message: '请选择资产类型' }]}>
                <Select options={Object.entries(ASSET_TYPE_META).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="assetName"
                label="资产名称"
                rules={[{ required: true, message: '请填写资产名称' }, { max: 128, message: '不超过 128 字' }]}
              >
                <Input maxLength={128} placeholder="如：医用腕带打印机" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="spec" label="规格型号" rules={[{ max: 255, message: '不超过 255 字' }]}>
                <Input maxLength={255} placeholder="如：Zebra ZD888 / 50mm×30mm" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="providerName" label="提供方" rules={[{ max: 128, message: '不超过 128 字' }]}>
                <Input maxLength={128} placeholder="默认按中标方免费提供" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="qty" label="发放数量" rules={[{ required: true, message: '请填写数量' }]}>
                <InputNumber min={0.001} step={1} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unit" label="计量单位" rules={[{ max: 32, message: '不超过 32 字' }]}>
                <Input maxLength={32} placeholder="台 / 卷 / 个" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="issueDate" label="发放日期" rules={[{ required: true, message: '请选择发放日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="receiverOrgId" label="受赠机构" rules={[{ required: true, message: '请选择受赠机构' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择机构"
                  options={(orgs.data ?? []).map((row) => ({ value: row.id, label: row.orgName }))}
                  onChange={() => form.setFieldsValue({ receiverDeptId: undefined })}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="receiverDeptId" label="受赠科室" extra="可选；不选表示机构层面接收">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={formReceiverOrgId ? '选择科室' : '先选择机构'}
                  disabled={!formReceiverOrgId}
                  loading={depts.isFetching}
                  options={(depts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="voucherUrl" label="凭证 URL" rules={[{ max: 500, message: '不超过 500 字' }]}>
            <Input maxLength={500} placeholder="签收单 / 发放凭证影像地址（可选）" />
          </Form.Item>
          <Form.Item name="remark" label="备注" rules={[{ max: 500, message: '不超过 500 字' }]}>
            <Input.TextArea rows={2} maxLength={500} showCount placeholder="安装位置、后期增设说明等" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
